import React, { useEffect } from "react";

import {
    RiDraggable, RiInsertColumnRight,
} from "@remixicon/react";

import { ChatStatus, DashboardTicketStateInput, useGetTicketStatesQuery } from "../../../../__generated__/api";
import { LoadingIcon } from "../../../loading/Loading";
import Toggle from "../../../theme/toggle/Toggle";
import SortableList from "../../../theme/sortable/SortableList";

export default function ColumnSettings({
    value,
    onChange,
}: {
    value: DashboardTicketStateInput[]
    onChange(value: DashboardTicketStateInput[]): void
}) {
    const { data, loading } = useGetTicketStatesQuery();

    useEffect(() => {
        if (data?.ticketStates && (!value || value.isEmpty)) {
            const requested = data?.ticketStates.find(row => row.chatStatuses.includes(ChatStatus.ChatRequested));
            const assigned = data?.ticketStates.find(row => row.chatStatuses.includes(ChatStatus.Assigned));
            const onHold = data?.ticketStates.find(row => row.chatStatuses.includes(ChatStatus.OnHold));
            const finished = data?.ticketStates.find(row => row.chatStatuses.includes(ChatStatus.Finished));

            const defaultList = [requested, assigned, onHold, finished].filterNull();
            if (defaultList.isEmpty) {
                defaultList.push(data?.ticketStates.firstElement);
            }
            onChange(defaultList.map((row, index) => ({
                ticketStateId: row.id,
                locationIndex: index,
            })));
        }
    }, [value, data?.ticketStates]);

    if (loading || !data) {
        return <LoadingIcon />;
    }
    const selectedSet = new Set(value.toIdList("ticketStateId"));

    const allStates = data?.ticketStates.toIdMap("id");

    const selectedItems = value.map(settings => allStates[settings.ticketStateId as string])
        .filterNull();
    const remainingStates = data.ticketStates.filter(row => !selectedSet.has(row.id));

    return (
        <SortableList
            id="column-settings"
            items={selectedItems}
            onReorder={items => {
                onChange(items.map((row, index) => ({
                    ticketStateId: row.id,
                    locationIndex: index,
                })));
            }}
            className="d-flex flex-column gap3-2 text-body-2-medium"
        >
            {selectedItems.map(ticketState => (
                <SortableList.Item
                    key={ticketState.id}
                    item={ticketState}
                    className="d-flex justify-content-between align-items-baseline"
                >
                    <div className="text-body-primary text-truncate">
                        <span className="m3e-1 icon-secondary">
                            <RiDraggable size={20} />
                        </span>
                        {ticketState.name}
                    </div>
                    <Toggle
                        size="small"
                        checked={selectedSet.has(ticketState.id)}
                        onChange={() => {
                            onChange(value.filter(row => row.ticketStateId !== ticketState.id));
                        }}
                    />
                </SortableList.Item>
            ))}
            {remainingStates.isNotEmpty && (
                <div className="text-body-1-semibold m3y-1" />
            )}
            {remainingStates.map(ticketState => (
                <SortableList.Item
                    key={ticketState.id}
                    item={ticketState}
                    className="d-flex justify-content-between align-items-baseline"
                >
                    <div className="text-body-primary text-truncate p3e-1">
                        <span className="m3e-1 icon-secondary">
                            <RiInsertColumnRight size={20} />
                        </span>
                        {ticketState.name}
                    </div>
                    <Toggle
                        size="small"
                        checked={false}
                        onChange={() => {
                            onChange([...value, {
                                ticketStateId: ticketState.id,
                                locationIndex: value.length,
                            }]);
                        }}
                    />
                </SortableList.Item>
            ))}

        </SortableList>
    );
}
